import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PRODUCTION_DOMAIN = 'https://www.glowlawns.com';
const SITE_NAME = 'Glow Lawns';

// Page-specific SEO data
const PAGE_SEO = {
  '/': {
    title: 'Glow Lawns | Tree House Nature Resort in Moonrise Valley, Kerala',
    description: 'Peaceful tree house resort in Chappanthottam, Kavilumpara, Kerala. Experience forest stays, private waterfall, fresh organic food & moonrise views.'
  },
  '/about': {
    title: 'About Us | Glow Lawns Nature Resort Kerala',
    description: 'Discover the story of Glow Lawns - a forest-based nature retreat in Moonrise Valley, Kerala. Born from love for nature and sustainable hospitality.'
  },
  '/stay': {
    title: 'Stay With Us | Tree Houses & Mud Houses | Glow Lawns Kerala',
    description: 'Book luxury tree house cottages, earth-luxe mud houses, or private stone villas at Glow Lawns. Premium forest accommodations in Moonrise Valley, Kerala.'
  },
  '/experiences': {
    title: 'Off-Roading & Forest Experiences | GlowLAWNS Kerala | Poilochal Jeep Trail',
    description: 'Experience authentic Kerala 4x4 off-road adventures at Poilochal Chappanthottam. Star gazing, jeep trails, uphill climbs & nature experiences at GlowLAWNS Moonrise Valley.',
    keywords: 'GlowLAWNS Off Roading, Poilochal Chappanthottam Jeep Trail, Wayanad 4x4 Adventure, Kerala Uphill Off Road Experience, Moonrise Valley Jeep Ride'
  },
  '/dining': {
    title: 'Food & Dining | Farm-Fresh Kerala Cuisine | Glow Lawns',
    description: 'Enjoy fresh, organic Kerala cuisine at Glow Lawns. Farm-to-table meals prepared with local ingredients in our forest restaurant.'
  },
  '/events': {
    title: 'Events & Celebrations | Glow Lawns Nature Resort Kerala',
    description: 'Host intimate weddings, team offsites, and celebrations at Glow Lawns. Unique forest venue in Moonrise Valley, Kerala.'
  },
  '/sustainability': {
    title: 'Sustainability & Community | Glow Lawns Eco Resort',
    description: 'Learn about our commitment to eco-friendly practices and community support at Glow Lawns nature resort in Kerala.'
  },
  '/gallery': {
    title: 'Photo Gallery | Glow Lawns Nature Resort Kerala',
    description: 'View stunning photos of tree houses, forest views, waterfall, and experiences at Glow Lawns resort in Moonrise Valley, Kerala.'
  },
  '/reviews': {
    title: 'Guest Reviews | Glow Lawns Nature Resort Kerala',
    description: 'Read authentic guest reviews and testimonials from visitors to Glow Lawns tree house resort in Kerala.'
  },
  '/feedback': {
    title: 'Share Your Feedback | Glow Lawns Kerala',
    description: 'We value your feedback. Share your experience at Glow Lawns nature resort to help us serve you better.'
  },
  '/contact': {
    title: 'Contact Us | Book Your Stay | Glow Lawns Kerala',
    description: 'Get in touch with Glow Lawns to check availability, pricing, or plan your perfect forest retreat in Moonrise Valley, Kerala. Quick response guaranteed.'
  },
  '/policies': {
    title: 'Resort Policies | Glow Lawns Kerala',
    description: 'View the resort policies and guidelines for your stay at Glow Lawns including check-in/out times, cancellation policy, and house rules.'
  }
};

/**
 * Custom hook for comprehensive SEO management
 * Sets title, meta description, canonical URL, and Open Graph tags
 */
export const useSEO = (customPath = null) => {
  const location = useLocation();
  
  useEffect(() => {
    const path = customPath || location.pathname;
    const seo = PAGE_SEO[path] || PAGE_SEO['/'];
    const canonicalUrl = `${PRODUCTION_DOMAIN}${path === '/' ? '' : path}`;
    
    // Update page title
    document.title = seo.title;
    
    // Update or create meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content = seo.description;
    }
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.href = canonicalUrl;
    }
    
    // Update Open Graph tags
    const ogTags = {
      'og:title': seo.title,
      'og:description': seo.description,
      'og:url': canonicalUrl
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (meta) {
        meta.content = content;
      }
    });
    
    // Update Twitter tags
    const twitterTags = {
      'twitter:title': seo.title,
      'twitter:description': seo.description,
      'twitter:url': canonicalUrl
    };
    
    Object.entries(twitterTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (meta) {
        meta.content = content;
      }
    });
    
  }, [location.pathname, customPath]);
};

/**
 * Site Navigation schema for search engines
 */
export const siteNavigationSchema = {
  "@context": "https://schema.org",
  "@type": "SiteNavigationElement",
  "name": "Main Navigation",
  "url": PRODUCTION_DOMAIN,
  "hasPart": [
    { "@type": "WebPage", "name": "Home", "url": `${PRODUCTION_DOMAIN}/` },
    { "@type": "WebPage", "name": "About", "url": `${PRODUCTION_DOMAIN}/about` },
    { "@type": "WebPage", "name": "Stay With Us", "url": `${PRODUCTION_DOMAIN}/stay` },
    { "@type": "WebPage", "name": "Experiences", "url": `${PRODUCTION_DOMAIN}/experiences` },
    { "@type": "WebPage", "name": "Dining", "url": `${PRODUCTION_DOMAIN}/dining` },
    { "@type": "WebPage", "name": "Events", "url": `${PRODUCTION_DOMAIN}/events` },
    { "@type": "WebPage", "name": "Sustainability", "url": `${PRODUCTION_DOMAIN}/sustainability` },
    { "@type": "WebPage", "name": "Gallery", "url": `${PRODUCTION_DOMAIN}/gallery` },
    { "@type": "WebPage", "name": "Reviews", "url": `${PRODUCTION_DOMAIN}/reviews` },
    { "@type": "WebPage", "name": "Contact", "url": `${PRODUCTION_DOMAIN}/contact` }
  ]
};

export default useSEO;
